/**
 * stripQuotedReply — return only the dealer's freshly written text from an
 * inbound email body, stripping the quoted thread, our worker's bracket
 * annotations, the DooCars footer, and the subject echo (`Re: ...`).
 *
 * Conservative: only cuts at well-known markers. If no marker is found the
 * input is returned unchanged (apart from leading/trailing whitespace).
 *
 * @param {string} text  Raw message.content as stored on negotiation_messages.
 * @param {object} [opts]
 * @param {string} [opts.dealerName]  When provided, leading lines that exactly
 *                                    match the dealer's name (the bubble's
 *                                    role label already shows it) are dropped.
 * @returns {string}
 */
export function stripQuotedReply(text, opts = {}) {
  if (!text || typeof text !== 'string') return '';

  // ---- 1. Cut everything from the first reply boundary onward. ----
  // Markers we look for, anywhere in the body:
  //   " ---- On Mon, ... wrote ---- "  (Zoho / generic dash-wrapped form)
  //   "On Mon, ... wrote:"             (Gmail / Outlook standard)
  //   "-----Original Message-----"     (Outlook)
  //   "----- Forwarded message -----"
  //   "From: ...\nSent: ..."           (Outlook header block)
  //   "[reply-to (alias): ..."          (worker-injected reply-to bracket)
  //   "[dev redirect — would have gone to ..."
  //   "Sent via DooCars"                (our own footer)
  //   First quoted line "> "
  const markers = [
    /^[ \t]*-{2,}[ \t]*On[ \t].+wrote[ \t]*-{2,}[ \t]*$/im,
    /^[ \t]*On[ \t].+wrote:?[ \t]*$/im,
    /^[ \t]*-+[ \t]*Original Message[ \t]*-+[ \t]*$/im,
    /^[ \t]*-+[ \t]*Forwarded message[ \t]*-+[ \t]*$/im,
    /^From:[ \t].+$/im,
    /^[ \t]*\[reply-to[ \t]*\(alias\)/im,
    /^[ \t]*\[dev redirect/im,
    /^[ \t]*Sent via DooCars/im,
    /^>+[ \t]?/im,
  ];

  let cutAt = text.length;
  for (const re of markers) {
    const m = text.match(re);
    if (m && typeof m.index === 'number' && m.index < cutAt) {
      cutAt = m.index;
    }
  }
  let result = text.slice(0, cutAt);

  // ---- 2. Strip leading subject echoes + dealer-name letterhead lines. ----
  // We only touch leading lines so a mid-message "Re:" is preserved.
  const dealerName = (opts.dealerName || '').trim().toLowerCase();
  const lines = result.split('\n');
  while (lines.length > 0) {
    const raw = lines[0];
    const line = raw.trim();
    if (line === '') {
      lines.shift();
      continue;
    }
    if (/^re:[ \t]/i.test(line)) {
      lines.shift();
      continue;
    }
    if (/^\[dev[ \t]*[→\->]/i.test(line)) {
      lines.shift();
      continue;
    }
    if (dealerName && line.toLowerCase() === dealerName) {
      lines.shift();
      continue;
    }
    break;
  }
  result = lines.join('\n');

  // ---- 3. Collapse runs of 3+ blank lines, trim ends. ----
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

export default stripQuotedReply;
